import { Box, Stack } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';

import { useUpdateTenantFromAdminMutation } from '../graphql/AdminConfigurationsPage.generated';

interface SCIMSectionProps {
  tenantId: string;
  existingWorkosDirectoryID: string | null;
  hasExistingWorkosDirectoryID: boolean;
  isTenantDetailsLoaded: boolean;
}

interface SCIMFormShape {
  workosDirectoryID: string;
}

export function SCIMSection({
  tenantId,
  existingWorkosDirectoryID,
  hasExistingWorkosDirectoryID,
  isTenantDetailsLoaded,
}: SCIMSectionProps) {
  const { showFeedback, createErrorFeedback } = useFeedback();

  const formMethods = useForm<SCIMFormShape>({
    defaultValues: {
      workosDirectoryID: existingWorkosDirectoryID ?? '',
    },
  });

  const [updateTenant, { loading }] = useUpdateTenantFromAdminMutation();

  const workosDirectoryIDValue = formMethods.watch('workosDirectoryID') ?? '';
  const trimmedWorkosDirectoryIDValue = workosDirectoryIDValue.trim();
  const canSaveSCIMConfiguration = Boolean(
    tenantId && trimmedWorkosDirectoryIDValue
  );
  const canClearSCIMConfigurationWithCurrentState =
    Boolean(tenantId) && isTenantDetailsLoaded && hasExistingWorkosDirectoryID;

  const onSubmit = useCallback(
    (formValues: SCIMFormShape) => {
      const value = formValues.workosDirectoryID.trim();
      if (!value) {
        return;
      }

      return updateTenant({
        variables: {
          input: {
            id: tenantId,
            update: {
              workosDirectoryID: value,
              workosUpdatedAt: new Date(),
            },
          },
        },
        onCompleted: () => {
          showFeedback('SCIM directory sync updated successfully', {
            variant: 'success',
          });
        },
        onError: createErrorFeedback('Failed to update SCIM directory sync'),
      });
    },
    [createErrorFeedback, showFeedback, tenantId, updateTenant]
  );

  const onClearSCIMConfiguration = useCallback(() => {
    if (!canClearSCIMConfigurationWithCurrentState) {
      return;
    }

    void updateTenant({
      variables: {
        input: {
          id: tenantId,
          update: {
            clearWorkosDirectoryID: true,
            clearWorkosUpdatedAt: true,
          },
        },
      },
      onCompleted: () => {
        formMethods.setValue('workosDirectoryID', '');
        showFeedback('SCIM directory sync cleared successfully', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback('Failed to clear SCIM directory sync'),
    });
  }, [
    canClearSCIMConfigurationWithCurrentState,
    createErrorFeedback,
    formMethods,
    showFeedback,
    tenantId,
    updateTenant,
  ]);

  return (
    <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
      <Stack spacing={2} maxWidth={400}>
        <Callout severity="info-high">
          Please disable JIT in the SSO settings when enabling SCIM.
        </Callout>
        <TextInput
          label="WorkOS Directory ID"
          placeholder="directory_YYY"
          {...formMethods.register('workosDirectoryID')}
        />
        <Box>
          <Stack direction="row" spacing={1}>
            <Button
              loading={loading}
              variant="primary"
              type="submit"
              size="sm"
              disabled={!canSaveSCIMConfiguration}
            >
              Save SCIM configuration
            </Button>
            <Button
              variant="destructive-secondary"
              type="button"
              size="sm"
              disabled={!canClearSCIMConfigurationWithCurrentState || loading}
              onClick={onClearSCIMConfiguration}
            >
              Clear SCIM configuration
            </Button>
          </Stack>
        </Box>
        {!isTenantDetailsLoaded && (
          <Callout severity="warning">
            Clear is unavailable until tenant details load successfully.
          </Callout>
        )}
      </Stack>
    </Box>
  );
}
